import React, { useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import api from "../api";
import type { ProjectSummary, PipelineRun } from "../api";
import { useApi } from "../useApi";
import { Dot } from "../components/shared/Dot";
import { eC, dC, timeAgo, formatDuration } from "../lib/helpers";

export function RunsPage({ projects }: { projects: ProjectSummary[] }) {
  const [selectedProject, setSelectedProject] = useState(projects[0]?.project_id || "");
  const { data: runs, loading, error, refetch } = useApi(
    () => selectedProject ? api.listRuns(selectedProject, 50).catch(() => []) : Promise.resolve([]),
    [selectedProject]
  );
  const [selRun, setSelRun] = useState<PipelineRun | null>(null);

  return (
    <div>
      <div className="page-title-row">
        <h2 className="page-title">Pipeline Runs</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select className="input input-mono" style={{ width: "auto", fontSize: "var(--text-sm)" }} value={selectedProject} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setSelectedProject(e.target.value); setSelRun(null); }}>
            {projects.filter((p: ProjectSummary) => p.project_id !== "example-app").map((p: ProjectSummary) => (
              <option key={p.project_id} value={p.project_id}>{p.display_name}</option>
            ))}
          </select>
          <button className="btn btn-sm" onClick={refetch}><RefreshCw size={11} /></button>
        </div>
      </div>

      {error && (
        <div className="card" style={{ background: "var(--red-dim)", borderColor: "var(--red)", marginBottom: 12, padding: "8px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={14} style={{ color: "var(--red)" }} />
            <span style={{ fontSize: "var(--text-sm)", color: "var(--red)", flex: 1 }}>{error}</span>
            <button className="btn btn-sm" onClick={refetch}><RefreshCw size={11} /> Retry</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="empty-state"><div className="spinner" /><span className="muted">Loading runs...</span></div>
      ) : runs && runs.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 10 }}>
          <div>
            {runs.map((r: PipelineRun) => (
              <div key={r.id} onClick={() => setSelRun(r)} className="card" style={{
                cursor: "pointer", marginBottom: 4, padding: 10,
                borderColor: selRun?.id === r.id ? "var(--orange)" : "var(--border)",
                background: selRun?.id === r.id ? "var(--orange-dim)" : "var(--surface)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <Dot status={r.status} sz={5} />
                  <span style={{ fontSize: "var(--text-xs)", fontWeight: 500, color: "var(--text)", flex: 1 }}>{r.trigger}{r.issue_number ? ` #${r.issue_number}` : ""}</span>
                  <span className="mono muted" style={{ fontSize: "var(--text-xs)" }}>{r.started_at ? timeAgo(r.started_at) : ""}</span>
                </div>
                <div className="mono muted" style={{ fontSize: "var(--text-xs)", marginTop: 3 }}>
                  {r.route && <span className="badge" style={{ background: `${eC(r.route.replace("claude_", ""))}15`, color: eC(r.route.replace("claude_", "")) }}>{r.route}</span>}
                  {r.duration_ms ? ` · ${formatDuration(r.duration_ms)}` : ""}
                  {r.cost != null ? ` · $${r.cost.toFixed(2)}` : ""}
                </div>
              </div>
            ))}
          </div>
          <div>
            {selRun ? (
              <div className="card">
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <Dot status={selRun.status} />
                    <span style={{ fontSize: "var(--text-base)", fontWeight: 500, color: "var(--text)" }}>{selRun.trigger}{selRun.issue_number ? ` #${selRun.issue_number}` : ""}</span>
                  </div>
                  <div className="mono muted" style={{ fontSize: "var(--text-xs)" }}>{selRun.id}</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                  <div><span className="section-label">Status</span><div style={{ fontSize: "var(--text-sm)", color: dC(selRun.status), fontWeight: 500 }}>{selRun.status}</div></div>
                  <div><span className="section-label">Route</span><div style={{ fontSize: "var(--text-sm)", color: "var(--text)" }}>{selRun.route || "—"}</div></div>
                  <div><span className="section-label">Duration</span><div className="mono" style={{ fontSize: "var(--text-sm)", color: "var(--text)" }}>{selRun.duration_ms ? formatDuration(selRun.duration_ms) : "—"}</div></div>
                  <div><span className="section-label">Cost</span><div className="mono" style={{ fontSize: "var(--text-sm)", color: "var(--text)" }}>{selRun.cost != null ? `$${selRun.cost.toFixed(2)}` : "—"}</div></div>
                  <div><span className="section-label">Branch</span><div className="mono" style={{ fontSize: "var(--text-sm)", color: "var(--text)" }}>{selRun.branch || "—"}</div></div>
                  <div><span className="section-label">PR</span><div style={{ fontSize: "var(--text-sm)", color: "var(--text)" }}>#{selRun.pr_number || "—"}</div></div>
                </div>
                {selRun.issue_text && (
                  <div className="card mono" style={{ padding: 10, background: "var(--surface2)", fontSize: "var(--text-sm)", color: "var(--text)", whiteSpace: "pre-wrap" }}>{selRun.issue_text}</div>
                )}
                {selRun.error && (
                  <div className="card" style={{ padding: 10, background: "var(--red-dim)", borderColor: "var(--red)", fontSize: "var(--text-sm)", color: "var(--red)", marginTop: 8 }}>{selRun.error}</div>
                )}
                {selRun.status === 'failed' && (
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 8 }}>
                    Encountering a persistent issue?{' '}
                    <button
                      onClick={() => (window as any).LumenIQSupport?.open()}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--text-muted)', textDecoration: 'underline',
                        fontSize: 'var(--text-xs)', fontFamily: 'inherit', padding: 0,
                      }}
                    >
                      Contact support
                    </button>
                    {' '}&mdash; include your pipeline ID and error message for fastest help.
                  </p>
                )}
              </div>
            ) : (
              <div className="empty-state" style={{ height: 200, border: "1px dashed var(--border)", borderRadius: "var(--radius-xl)" }}>
                <span className="muted">Select a run</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="empty-state" style={{ border: "1px dashed var(--border)", borderRadius: "var(--radius-lg)" }}>
          <span className="muted" style={{ fontSize: "var(--text-sm)" }}>
            No pipeline runs for {selectedProject}. Create an issue with the <span className="badge badge-orange">auto</span> label to trigger one.
          </span>
        </div>
      )}
    </div>
  );
}
